import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { baseUrl, getValidAccessToken, mapKnownBackendError } from "../shared";
import languageMapper from "../shared";
import { useTranslation } from "react-i18next";
import "../i18n";
import Navbaranonuser from "../navbaranonuser";
import Navbar from "../navbar";

function ListRequests() {
  const { auth } = useContext(AuthContext);
  const [requestedTerms, setRequestedTerms] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();


  // Claude: bugfix reported directly — "'Requested terms' should be available for all registered
  // Claude: users." This used to redirect anyone who wasn't specifically 'administrator' or
  // Claude: 'editor' — even an r_user or approver with a valid session got bounced back to "/".
  // Claude: The backend (get_requested_terms) only ever required IsAuthenticated anyway, so this
  // Claude: brings the frontend in line with what already worked for any logged-in role.
  // Claude: alpha-tester feedback — "Requested terms cannot survive refresh... silently redirects
  // Claude: home, although the account remains signed in." On a fresh page load this effect fires
  // Claude: before AuthContext has restored the session from localStorage (child effects run before
  // Claude: parent effects — same issue Logout.js documents), so auth.userName is still null for a
  // Claude: genuinely logged-in user. Only redirect when there is no stored session either; once
  // Claude: AuthContext hydrates, auth changes and this effect re-runs and fetches.
  useEffect(() => {
    if (auth?.userName == null) {
      if (!localStorage.getItem("accessToken")) {
        navigate("/"); // Redirect anonymous visitors to the home page
      }
    } else {
      fetchRequestedTerms();
    }
  }, [auth]);

  const fetchRequestedTerms = async () => {
    const token = await getValidAccessToken(); // Get valid token

    try {
      const response = await fetch(`${baseUrl}v1/requested-terms/`, {
        headers: {
          // Claude: bugfix found during UAT — this sent the possibly-stale auth.accessToken from
          // context instead of the `token` just resolved above. getValidAccessToken() silently
          // refreshes an expired token in localStorage but doesn't update AuthContext's state, so
          // once the original login's access token expired, this always sent an invalid token and
          // failed with the generic "Please log in again" message even though a valid token
          // existed. See also the identical bug fixed in PhraseDetail.js's confirmDelete.
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setRequestedTerms(data.requested_terms);
      } else if (data.error) {
        // Claude: a server-provided message (e.g. a permission error) isn't necessarily about an
        // Claude: expired login — shown as-is, same as before, with no login link added.
        setError(data.error);
      } else {
        // Claude: alpha-tester feedback — "the error message is displayed... but there is no link
        // Claude: to the login page." Same pattern UserRoleManager.js already uses. This fallback
        // Claude: (no server-provided message) is the case the comment above describes — reached on
        // Claude: an auth failure — so the login link belongs here specifically.
        setError(
          <>
            Failed to fetch requested terms. Please{' '}
            <Link to="/login" className="text-blue-500 underline hover:text-blue-700">log in</Link> again.
          </>
        );
      }
    } catch (err) {
      setError(t("something_went_wrong_fetching_terms"));
    }
  };

  // Claude: alpha-tester feedback — "because access is relatively open, malicious users can
  // Claude: request inappropriate word[s], and there should be a way to address that problem."
  // Claude: This page previously had no way to reject a request outright, only to fulfil it (via
  // Claude: "Add translation", which happens to also delete the original request as a side effect —
  // Claude: see AddRequest.js's handleSubmitWord). Reuses that same DELETE endpoint directly
  // Claude: (delete_requested_term, IsAuthenticated — deliberately not narrowed further; see its
  // Claude: comment in views.py for why) — the gap being addressed is the missing button, not the
  // Claude: endpoint's own permission level. Removes the row locally on success rather than
  // Claude: refetching the whole list.
  const handleDeleteRequest = async (termId) => {
    if (!window.confirm(t("delete_request_confirm"))) {
      return;
    }
    const token = await getValidAccessToken();
    try {
      const response = await fetch(`${baseUrl}v1/delete-request-term/${termId}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setRequestedTerms((previous) => previous.filter((term) => term.id !== termId));
      } else {
        const data = await response.json();
        alert(mapKnownBackendError(t, data.error) || t("could_not_delete_request"));
      }
    } catch (err) {
      alert(t("could_not_delete_request"));
    }
  };

  // Claude: matches the moderation-role gating already used elsewhere in this app (e.g. the
  // Claude: Review queue/Duplicate cleanup Actions items in navbar.js) — a plain registered user
  // Claude: can still fulfil a request via "Add translation" (unaffected by this), just not reject
  // Claude: one outright.
  const canModerateRequests =
    auth.userRole === "editor" || auth.userRole === "approver" || auth.userRole === "administrator";

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    // <div className="min-h-screen bg-gray-100 p-8">
    //   <h1 className="text-4xl font-bold mb-6">Requested Terms</h1>
    //   <div className="overflow-x-auto">
    <div className="w-full min-h-screen bg-gray-100">
      {localStorage.setItem("bladsy", "ListRequests")}
      {auth.userName === null && <Navbaranonuser />}
      {auth.userName != null && <Navbar />}

      <div className="flex flex-col justify-center items-center p-2">
        <h1 className="text-4xl font-bold mb-6">
          <Link to={"/"}>
            {/* <a href="/" className="inline-block">  */}
            {t("requestlist")}
            {/* </a> */}
          </Link>
        </h1>
        <div className="w-full max-w-6xl bg-white rounded-lg shadow-lg p-3">
          <table className="table-auto w-full bg-white shadow-lg rounded-lg">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-4 text-left">{t("word")}</th>
                <th className="p-4 text-left">{t("suggested_translation")} </th>
                <th className="p-4 text-left">{t("target")} </th>
                <th className="p-4 text-left">{t("username")} </th>
                <th className="p-4 text-left">{t("date_requested")} </th>
                <th className="p-4 text-left">{t("action")} </th>
              </tr>
            </thead>
            <tbody>
              {requestedTerms.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-4">
                    {t("no_requests")}.
                  </td>
                </tr>
              ) : (
                (console.log("RequestedTerms: ", requestedTerms),
                requestedTerms.map((term) => (
                  <tr key={term.id} className="border-b">
                    <td className="p-4">{term.term}</td>
                    <td className="p-4">{term.suggested_term || "None"}</td>
                    <td className="p-4">
                      {languageMapper(term.target_language)}
                    </td>
                    <td className="p-4">{term.username}</td>
                    <td className="p-4">
                      {new Date(term.date_requested).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <Link
                        to={`/addrequest?termId=${encodeURIComponent(
                          term.id
                        )}&term=${encodeURIComponent(
                          term.term
                        )}&language=${encodeURIComponent(
                          term.target_language
                        )}&suggested=${encodeURIComponent(
                          term.suggested_term || ""
                        )}`}
                        className="bg-cyan-700 text-white py-2 px-4 rounded hover:bg-cyan-800"
                      >
                        {t("add_translation")}
                      </Link>
                      {canModerateRequests && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRequest(term.id)}
                          className="ml-2 bg-rose-700 text-white py-2 px-4 rounded hover:bg-rose-800"
                        >
                          {t("delete_request")}
                        </button>
                      )}
                    </td>
                  </tr>
                )))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ListRequests;
